/**
 * QuickDictCreate (31.08, владелец) — «+ новый расходник/ресурс» прямо из
 * дропдауна привязок (форма услуги/квеста/спецверсии): маленький поповер со
 * СТАНДАРТНЫМИ полями формы справочника (имя; у расходника — единица через
 * DictUnitCtl, как в «Справ.»). POST — те же /api/consumables|/api/resources
 * с клиентским id (идемпотентный повтор, retries) — новая строка появится и
 * в «Справ.» после его загрузки. onCreated отдаёт созданную строку хосту —
 * тот кладёт её в локальный каталог и сразу привязывает.
 */
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "../../api/client";
import type { ApiOk } from "../../api/types";
import { DictUnitCtl } from "./DictUnitCtl";
import { dictErr } from "./dictErr";
import cfg from "./config.module.css";
import qcss from "./quests.module.css";
import css from "./dicts.module.css";

const newId = (kind: "c" | "r"): string =>
  `${kind === "c" ? "con" : "r"}_${Date.now().toString(36)}${Math.floor(Math.random() * 1296).toString(36)}`;

export interface QuickCreated { id: string; name: string; unit: string }

export function QuickDictCreate({ kind, anchor, onClose, onCreated }: {
  kind: "c" | "r";
  anchor: HTMLElement | null;
  onClose: () => void;
  onCreated: (row: QuickCreated) => void;
}) {
  const [n, setN] = useState("");
  const [u, setU] = useState("шт");
  const [custom, setCustom] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const idRef = useRef(newId(kind)); // один id на сессию формы — повтор «Создать» не дублит
  const popRef = useRef<HTMLDivElement>(null);

  /* Закрытие тапом мимо — pointerdown capture (канон поповеров). */
  useEffect(() => {
    if (!anchor) return;
    const onDown = (e: PointerEvent) => {
      const t = e.target as HTMLElement | null;
      if (popRef.current && t && !popRef.current.contains(t)) onClose();
    };
    document.addEventListener("pointerdown", onDown, true);
    return () => document.removeEventListener("pointerdown", onDown, true);
  }, [anchor, onClose]);

  if (!anchor) return null;
  const r = anchor.getBoundingClientRect();
  const top = Math.min(r.bottom + 6, window.innerHeight - 150);
  const left = Math.min(Math.max(6, r.left), Math.max(6, window.innerWidth - 246));

  const submit = () => {
    const name = n.trim();
    if (busy || !name || (kind === "c" && !u.trim())) return;
    setBusy(true); setErr(null);
    const body = kind === "c" ? { id: idRef.current, name, unit: u.trim() } : { id: idRef.current, name };
    void api<ApiOk>(kind === "c" ? "/api/consumables" : "/api/resources", { method: "POST", retries: 2, body })
      .then(() => { onCreated({ id: idRef.current, name, unit: u.trim() }); onClose(); })
      .catch((e: unknown) => { setBusy(false); setErr(dictErr(e)); });
  };

  return createPortal(
    <div ref={popRef} className={css.qcPop} style={{ top, left }}>
      <div className={css.qcTitle}>{kind === "c" ? "Новый расходник" : "Новый ресурс"}</div>
      <input
        className={`${qcss.txt} ${css.qcName}`} maxLength={200} autoFocus
        placeholder="Название" value={n} onChange={(e) => setN(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
      />
      {kind === "c" ? (
        <div className={css.qcUnit}>
          <span className={cfg.klbl}>Единица</span>
          <DictUnitCtl u={u} custom={custom} onChange={(nu, nc) => { setU(nu); setCustom(nc); }} />
        </div>
      ) : null}
      {err ? <div className={css.qcErr}>Не создалось: {err}.</div> : null}
      <div className={css.qcBtns}>
        <button type="button" className={`${qcss.abtn} ${qcss.cf}`} disabled={busy || !n.trim()} onClick={submit}>
          {busy ? "Создаём…" : "Создать"}
        </button>
        <button type="button" className={qcss.abtn} onClick={onClose}>Отмена</button>
      </div>
    </div>,
    document.body,
  );
}
